/**
 * Employees List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  MoreVert as MoreIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, StatsCard } from '../../components/common';
import {
  useGetEmployeesQuery,
  useDeleteEmployeeMutation,
  useActivateEmployeeMutation,
  useSuspendEmployeeMutation,
  useDeactivateEmployeeMutation,
  useResendEmployeeCredentialsMutation,
  useGetRolesQuery,
} from '../../store/api/rbacApi';
import { Employee } from '../../types/rbac';
import { palette } from '../../theme/palette';
import moment from 'moment';
import toast from 'react-hot-toast';

const EmployeesListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'suspended' | ''>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuEmployee, setMenuEmployee] = useState<Employee | null>(null);

  const { data, isLoading } = useGetEmployeesQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    role_id: roleFilter || undefined,
    status: statusFilter || undefined,
  });

  const { data: rolesData } = useGetRolesQuery();

  const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();
  const [activateEmployee] = useActivateEmployeeMutation();
  const [suspendEmployee] = useSuspendEmployeeMutation();
  const [deactivateEmployee, { isLoading: isDeactivating }] = useDeactivateEmployeeMutation();
  const [resendCredentials] = useResendEmployeeCredentialsMutation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, employee: Employee) => {
    setAnchorEl(event.currentTarget);
    setMenuEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuEmployee(null);
  };

  const handleDelete = async () => {
    if (!selectedEmployee) return;
    try {
      await deleteEmployee(selectedEmployee.id).unwrap();
      toast.success(t('rbac.employees.toast.deleteSuccess', { name: selectedEmployee.user_name }));
      setDeleteDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error: any) {
      console.error('Failed to delete employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.deleteError'));
    }
  };

  const handleActivate = async (employee: Employee) => {
    try {
      await activateEmployee(employee.id).unwrap();
      toast.success(t('rbac.employees.toast.activateSuccess', { name: employee.user_name }));
      handleMenuClose();
    } catch (error: any) {
      console.error('Failed to activate employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.activateError'));
    }
  };

  const handleSuspend = async (employee: Employee) => {
    try {
      await suspendEmployee(employee.id).unwrap();
      toast.success(t('rbac.employees.toast.suspendSuccess', { name: employee.user_name }));
      handleMenuClose();
    } catch (error: any) {
      console.error('Failed to suspend employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.suspendError'));
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedEmployee) return;
    try {
      await deactivateEmployee(selectedEmployee.id).unwrap();
      toast.success(t('rbac.employees.toast.deactivateSuccess', { name: selectedEmployee.user_name }));
      setDeactivateDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error: any) {
      console.error('Failed to deactivate employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.deactivateError'));
    }
  };

  const handleResendCredentials = async (employee: Employee) => {
    try {
      await resendCredentials(employee.id).unwrap();
      toast.success(t('rbac.employees.toast.credentialsSuccess', { email: employee.user_email }));
      handleMenuClose();
    } catch (error: any) {
      console.error('Failed to resend credentials:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.credentialsError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { bg: palette.status.success + '20', color: palette.status.success };
      case 'suspended':
        return { bg: palette.status.warning + '20', color: palette.status.warning };
      case 'inactive':
        return { bg: '#E2E8F0', color: '#64748B' };
      default:
        return { bg: '#E2E8F0', color: '#64748B' };
    }
  };

  const columns: Column<Employee>[] = [
    {
      id: 'user_name',
      label: t('rbac.employees.cols.employee'),
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Box>
            <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
              {row.user_name}
            </Box>
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {row.user_email}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('rbac.employees.idPrefix', { id: row.employee_id })}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      id: 'role_name',
      label: t('rbac.employees.cols.role'),
      minWidth: 150,
      render: (row) => (
        <Chip
          label={row.role_name}
          size="small"
          sx={{
            backgroundColor: palette.brand[50],
            color: palette.brand[700],
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: 'department',
      label: t('rbac.employees.cols.department'),
      minWidth: 150,
      render: (row) => row.department || '-',
    },
    {
      id: 'position',
      label: t('rbac.employees.cols.position'),
      minWidth: 150,
      render: (row) => row.position || '-',
    },
    {
      id: 'hire_date',
      label: t('rbac.employees.cols.hireDate'),
      minWidth: 120,
      render: (row) => moment(row.hire_date).format('MMM DD, YYYY'),
    },
    {
      id: 'last_login_at',
      label: t('rbac.employees.cols.lastLogin'),
      minWidth: 120,
      render: (row) =>
        row.last_login_at ? moment(row.last_login_at).fromNow() : t('rbac.employees.never'),
    },
    {
      id: 'status',
      label: t('rbac.employees.cols.status'),
      minWidth: 100,
      align: 'center',
      render: (row) => {
        const statusColors = getStatusColor(row.status);
        return (
          <Chip
            label={t(`rbac.status.${row.status}`)}
            size="small"
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      label: t('common.actions'),
      minWidth: 120,
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title={t('rbac.employees.tooltip.view')}>
            <IconButton
              size="small"
              onClick={() => navigate(`/employees/${row.id}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('rbac.employees.tooltip.edit')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/employees/${row.id}/edit`)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('rbac.employees.tooltip.more')}>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row)}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const stats = data?.results
    ? [
        {
          title: t('rbac.employees.stats.total'),
          value: data.count.toString(),
          icon: <PeopleIcon />,
          color: palette.brand[500],
        },
        {
          title: t('rbac.employees.stats.active'),
          value: data.results.filter((e) => e.status === 'active').length.toString(),
          icon: <ActivateIcon />,
          color: palette.status.success,
        },
        {
          title: t('rbac.employees.stats.suspended'),
          value: data.results.filter((e) => e.status === 'suspended').length.toString(),
          icon: <BlockIcon />,
          color: palette.status.warning,
        },
        {
          title: t('rbac.employees.stats.inactive'),
          value: data.results.filter((e) => e.status === 'inactive').length.toString(),
          icon: <BlockIcon />,
          color: '#64748B',
        },
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('rbac.employees.title')}
        subtitle={t('rbac.employees.subtitle')}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/employees/create')}
          >
            {t('rbac.employees.addButton')}
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('rbac.employees.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('rbac.employees.filters.role')}</InputLabel>
            <Select
              value={roleFilter}
              label={t('rbac.employees.filters.role')}
              onChange={(e) => setRoleFilter(e.target.value as number | '')}
            >
              <MenuItem value="">{t('rbac.employees.filters.allRoles')}</MenuItem>
              {rolesData?.results?.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('rbac.employees.filters.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('rbac.employees.filters.status')}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <MenuItem value="">{t('rbac.employees.filters.allStatus')}</MenuItem>
              <MenuItem value="active">{t('rbac.status.active')}</MenuItem>
              <MenuItem value="suspended">{t('rbac.status.suspended')}</MenuItem>
              <MenuItem value="inactive">{t('rbac.status.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuEmployee?.status !== 'active' && (
          <MenuItem onClick={() => menuEmployee && handleActivate(menuEmployee)}>
            <ListItemIcon>
              <ActivateIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('rbac.employees.menu.activate')}</ListItemText>
          </MenuItem>
        )}
        {menuEmployee?.status === 'active' && (
          <MenuItem onClick={() => menuEmployee && handleSuspend(menuEmployee)}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>{t('rbac.employees.menu.suspend')}</ListItemText>
          </MenuItem>
        )}
        {(menuEmployee?.status === 'active' || menuEmployee?.status === 'suspended') && (
          <MenuItem
            onClick={() => {
              setSelectedEmployee(menuEmployee);
              setDeactivateDialogOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PersonOffIcon fontSize="small" color="action" />
            </ListItemIcon>
            <ListItemText>{t('rbac.employees.menu.deactivate')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => menuEmployee && handleResendCredentials(menuEmployee)}>
          <ListItemIcon>
            <EmailIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('rbac.employees.menu.resendCredentials')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedEmployee(menuEmployee);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('rbac.employees.menu.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!isDeleting) setDeleteDialogOpen(false);
        }}
      >
        <DialogTitle>{t('rbac.employees.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <Alert severity="warning">
              {t('rbac.employees.dialog.deleteConfirm', { name: selectedEmployee.user_name })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? t('rbac.employees.buttons.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialogOpen}
        onClose={() => {
          if (!isDeactivating) setDeactivateDialogOpen(false);
        }}
      >
        <DialogTitle>{t('rbac.employees.dialog.deactivateTitle')}</DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <Alert severity="info">
              {t('rbac.employees.dialog.deactivateConfirm', { name: selectedEmployee.user_name })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)} disabled={isDeactivating}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            color="primary"
            variant="contained"
            disabled={isDeactivating}
          >
            {isDeactivating ? t('rbac.employees.buttons.deactivating') : t('rbac.employees.buttons.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesListPage;

